import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { EnumToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCountryMutations } from '~/hooks/use-countries';
import { countrySchema, EMPTY_COUNTRY, type CountryFormValues } from '~/lib/schemas/country';

interface CountryEditDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The country being edited, or null when adding a new one. */
	initial: CountryFormValues | null;
	classifications: EnumToken[];
}

/** Add / edit a country. RHF + zod, with a classification (`cls`) dropdown. */
export function CountryEditDialog({ open, onOpenChange, initial, classifications }: CountryEditDialogProps) {
	const { t } = useTranslation();
	const { create, update } = useCountryMutations();
	const form = useForm<CountryFormValues>({
		resolver: zodResolver(countrySchema) as Resolver<CountryFormValues>,
		defaultValues: initial ?? EMPTY_COUNTRY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_COUNTRY);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const cls = watch('cls');
	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.countries.editTitle') : t('system.countries.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.countries.formSubtitle')}</DialogDescription>
				</DialogHeader>

				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.countries.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.countries.columns.iso2')} error={formState.errors.iso2?.message}>
							<Input {...register('iso2')} maxLength={2} aria-invalid={Boolean(formState.errors.iso2)} />
						</FormField>
						<FormField label={t('system.countries.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<FormField label={t('system.countries.columns.classification')}>
						<Select
							value={cls === null ? '' : String(cls)}
							onValueChange={v => setValue('cls', v === '' ? null : Number(v), { shouldDirty: true })}>
							<SelectTrigger className="w-full" aria-label={t('system.countries.columns.classification')}>
								<SelectValue placeholder={t('system.countries.classificationPlaceholder')} />
							</SelectTrigger>
							<SelectContent>
								{classifications.map(c => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<FormField label={t('system.fields.erpCode')}>
						<Input {...register('erpcd')} />
					</FormField>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="country-visible">{t('system.countries.columns.visible')}</Label>
						<Switch id="country-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
